import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";

const MINIMIZED_KEY = "novaCompanionMinimized";

/**
 * How many bars the minimised launcher's wave draws.
 *
 * Deliberately a fraction of what the sheet's own meter runs (8-96, sized to the modal's measured
 * width): that one has a whole pill to spread across, this one has the inside of a 55px circle.
 * The chat downsamples its live levels to this count by bucketing rather than the circle picking
 * every Nth bar - a subset would drop whole regions of the spectrum, where bucketing keeps every
 * frequency represented in whichever bar it falls under.
 */
export const NOVA_VOICE_METER_BARS = 5;

/**
 * A snapshot of the running voice call, small enough to publish every frame.
 *
 * Declared here rather than imported from NovaCompanionChatComponent, which is what publishes it:
 * that component already imports this service, so the dependency has to point this way or the two
 * files form a cycle. It is also why this is a structure of primitives rather than a reference to
 * the component's own VoiceState union.
 */
export interface NovaVoiceMeter {
  /** True while Nova is speaking, false while she is listening or thinking. Picks the gradient. */
  speaking: boolean;
  /** Whether there is sound to draw right now. False through silence - see voiceHasSignal. */
  hasSignal: boolean;
  /** Bar heights as percentages, NOVA_VOICE_METER_BARS of them. */
  levels: number[];
  /** The chat's own voiceStatusLabel, verbatim - the only thing a screen reader gets. */
  label: string;
}

/**
 * Shared minimized/restored state for the Nova Companion launcher. Split out of
 * NovaCompanionFabComponent so the minimized state can be read from a second place -
 * NovaCompanionHeaderBadgeComponent (rendered inside PageHeroTitleComponent) - without
 * those two components needing to know about each other directly.
 *
 * When minimized, the full-size FAB (bottom-right) hides itself entirely and the small
 * badge docked in the page hero header (top-left, next to the page title) takes over as
 * the way back into the chat - rather than both rendering their own "mini" button in two
 * different places at once.
 *
 * localStorage-backed (same pattern as EmailConfirmationSnoozeService) so the choice
 * survives a page refresh - without this, minimizing would silently reset to the full FAB
 * on every reload, which reads as the app forgetting what the user just did.
 */
@Injectable({
  providedIn: "root",
})
export class NovaCompanionLauncherStateService {
  private minimizedSource = new BehaviorSubject<boolean>(
    localStorage.getItem(MINIMIZED_KEY) === "true"
  );
  minimized$: Observable<boolean> = this.minimizedSource.asObservable();

  /**
   * FLIP-234. Whether the chat sheet is currently collapsed, with a live voice session still
   * running behind it.
   *
   * A second, separate axis from minimized$ above, not a variant of it. That one is about which
   * *launcher* the user gets while the chat is closed; this one is about the chat being open but
   * collapsed. Both can be true at once - minimize the FAB, open Nova from the header badge,
   * start voice, dock it - and neither implies the other.
   *
   * Deliberately NOT localStorage-backed, which is the whole difference between the two. A
   * minimized launcher is a preference and should survive a refresh; a docked voice session is a
   * live microphone, an AudioContext and a recognizer, none of which survive a page load. A
   * persisted flag would restore the docked launcher on the next load with nothing behind it -
   * claiming Nova is listening when the session it belonged to no longer exists.
   *
   * The two readers treat it in opposite directions. NovaCompanionHeaderBadgeComponent hides while
   * it is true. NovaCompanionFabComponent SHOWS while it is true - the FAB circle is the minimised
   * voice state now, so it overrides minimized$ for as long as the call lasts; see that component
   * for why it has to.
   */
  private voiceDockedSource = new BehaviorSubject<boolean>(false);
  voiceDocked$: Observable<boolean> = this.voiceDockedSource.asObservable();

  /**
   * The live voice call, as much of it as the minimised launcher needs to draw itself. Null
   * whenever there is no docked call to describe.
   *
   * Published by NovaCompanionChatComponent - on every wave frame while docked, and on every
   * voiceState change - and consumed by NovaCompanionFabComponent. It exists because the FAB is
   * mounted in app.component.html while the chat is a MatDialog, so the two share no ancestor and
   * cannot be wired with inputs; this service is the only object they both hold.
   *
   * A BehaviorSubject rather than a plain Subject so a launcher subscribing mid-call paints
   * immediately rather than staying blank until the next frame.
   */
  private voiceMeterSource = new BehaviorSubject<NovaVoiceMeter | null>(null);
  voiceMeter$: Observable<NovaVoiceMeter | null> = this.voiceMeterSource.asObservable();

  /**
   * The minimised launcher asking the open-but-collapsed dialog to expand again.
   *
   * Needed because the FAB cannot reach the chat any other way: while docked the dialog is already
   * open, so dialog.open() would be wrong - it would stack a second Nova, with a second
   * microphone, over the running one - and there is no component reference to call restoreVoice()
   * on. A plain Subject, not a BehaviorSubject: this is an event, and a replayed one would expand
   * the sheet again the moment a later call docked.
   */
  private restoreRequestedSource = new Subject<void>();
  restoreRequested$: Observable<void> = this.restoreRequestedSource.asObservable();

  minimize(): void {
    localStorage.setItem(MINIMIZED_KEY, "true");
    this.minimizedSource.next(true);
  }

  restore(): void {
    localStorage.removeItem(MINIMIZED_KEY);
    this.minimizedSource.next(false);
  }

  /** Set by NovaCompanionChatComponent as it docks and undocks. */
  setVoiceDocked(docked: boolean): void {
    if (this.voiceDockedSource.value === docked) return;
    this.voiceDockedSource.next(docked);
  }

  /** Set by NovaCompanionChatComponent. Null clears the launcher back to Nova's mascot. */
  publishVoiceMeter(meter: NovaVoiceMeter | null): void {
    // Called up to 20 times a second, from outside the Angular zone. No equality check: the levels
    // array is rebuilt every frame by definition, so one would cost a comparison and never hit.
    this.voiceMeterSource.next(meter);
  }

  /** Called by NovaCompanionFabComponent when the user taps the minimised circle. */
  requestRestore(): void {
    this.restoreRequestedSource.next();
  }
}
